import { createSignal, createEffect, onMount, onCleanup, Switch, Match } from 'solid-js'
import { reconcile } from 'solid-js/store'
import { Router, A } from '@solidjs/router'
import { Portal } from 'solid-js/web'
import { ChevronDownOutline, GridOutline, PodiumOutline, CashOutline, RobotSolid, PeopleOutline, BarChartOutline, PersonOutline, LogoutOutline, KeyOutline, DesktopOutline, WalletOutline, HelpCircleOutline, ReceiptOutline, GroupLine, ChatboxEllipsesOutline } from '@/icons'
import { res2json } from '@/utils'
import Toast, { addToast } from '@/components/Toast'
import messenger, { events, WALLETS_APPLY, WALLETS_OPERATED, GAME_INFO, GAME_STARTED, GAME_PLAY, PLAYERS_JOIN } from '@/store/messenger'
import { playAlert, beep } from '@/store/play_alert'
import account, { keepalive, access } from '@/store/account'
import { PUSH_DENIED, PUSH_FAILED, PUSH_IOS_NOT_INSTALLED, PUSH_OK, PUSH_UNSUPPORTED, pushOn, togglePush, unsubscribePush } from '@/store/push'
import { NotificationsOutline, NotificationsOffOutline } from '@/icons'
import * as constants from '@lib/constants'
import * as games from '@games'
import { store, setStore } from '@/store'
import { EditPasswordDialog, ResetPassword } from '@/Password'
import Dashboard from '@/pages/Dashboard'
import Live from '@/pages/Live'
import Players from '@/pages/Players'
import Robots from '@/pages/Robots'
import Statistics from '@/pages/Statistics'
import Wallets from '@/pages/Wallets'
import Pending from '@/pages/Pending'
import Betting from '@/pages/Betting'
import StaffUsers from '@/pages/StaffUsers'
import Nob3Game from '@/pages/games/nob3/Game'
import Nob3Robot from '@/pages/games/nob3/Robot'
import Nob3RobotPlay from '@/pages/games/nob3/RobotPlay'
import Nob3RobotTiming from '@/pages/games/nob3/RobotTiming'
import Nob3BetRatio from '@/pages/games/nob3/BetRatio'
import Nob5Game from '@/pages/games/nob5/Game'
import Nob5Robot from '@/pages/games/nob5/Robot'
import Nob5RobotPlay from '@/pages/games/nob5/RobotPlay'
import Nob5RobotTiming from '@/pages/games/nob5/RobotTiming'
import Nob5BetRatio from '@/pages/games/nob5/BetRatio'
import Nob8Game from '@/pages/games/nob8/Game'
import Nob8Robot from '@/pages/games/nob8/Robot'
import Nob8RobotPlay from '@/pages/games/nob8/RobotPlay'
import Nob8RobotTiming from '@/pages/games/nob8/RobotTiming'
import Nob8BetRatio from '@/pages/games/nob8/BetRatio'

const routes = [
  { path: '/', component: () => (document.location.href = '/home') },
  { path: '/login/:code', component: LoggedIn },
  { path: '/home', component: Dashboard },
  { path: '/live', component: Live },
  { path: '/players', component: Players },
  { path: '/robots', component: Robots },
  { path: '/statistics', component: Statistics },
  { path: '/wallets', component: Wallets },
  { path: '/pending', component: Pending },
  { path: '/betting', component: Betting },
  { path: '/staff-users', component: StaffUsers },
  { path: '/settings/game', component: SettingsGame },
  { path: '/settings/robot', component: SettingsRobot },
  { path: '/settings/robot-play', component: SettingsRobotPlay },
  { path: '/settings/robot-timing', component: SettingsRobotTiming },
  { path: '/settings/bet-ratio', component: SettingsBetRatio },
  { path: '*', component: NotFound }
]

export default function App() {
  if (account().newly_registered) {
    return <ResetPassword />
  }
  return <Router root={Root}>{routes}</Router>;
}

function NotFound() {
  return <div>页面不存在</div>;
}

function LoggedIn() {
  if (account().username) {
    document.location.href = '/home'
    return
  }
  return <div>页面不存在</div>;
}

function SettingsGame() {
  const numbers = games.numbers[account().game]
  return (
    <Switch fallback={<div>页面不存在</div>}>
      <Match when={numbers === 3}><Nob3Game /></Match>
      <Match when={numbers === 5}><Nob5Game /></Match>
      <Match when={numbers === 8}><Nob8Game /></Match>
    </Switch>
  )
}

function SettingsRobot() {
  const numbers = games.numbers[account().game]
  return (
    <Switch fallback={<div>页面不存在</div>}>
      <Match when={numbers === 3}><Nob3Robot /></Match>
      <Match when={numbers === 5}><Nob5Robot /></Match>
      <Match when={numbers === 8}><Nob8Robot /></Match>
    </Switch>
  )
}

function SettingsRobotPlay() {
  const numbers = games.numbers[account().game]
  return (
    <Switch fallback={<div>页面不存在</div>}>
      <Match when={numbers === 3}><Nob3RobotPlay /></Match>
      <Match when={numbers === 5}><Nob5RobotPlay /></Match>
      <Match when={numbers === 8}><Nob8RobotPlay /></Match>
    </Switch>
  )
}

function SettingsRobotTiming() {
  const numbers = games.numbers[account().game]
  return (
    <Switch fallback={<div>页面不存在</div>}>
      <Match when={numbers === 3}><Nob3RobotTiming /></Match>
      <Match when={numbers === 5}><Nob5RobotTiming /></Match>
      <Match when={numbers === 8}><Nob8RobotTiming /></Match>
    </Switch>
  )
}

function SettingsBetRatio() {
  const numbers = games.numbers[account().game]
  return (
    <Switch fallback={<div>页面不存在</div>}>
      <Match when={numbers === 3}><Nob3BetRatio /></Match>
      <Match when={numbers === 5}><Nob5BetRatio /></Match>
      <Match when={numbers === 8}><Nob8BetRatio /></Match>
    </Switch>
  )
}

function Root({ children }) {
  const [numbers, setNumbers] = createSignal(0)

  const [drawerOn, setDrawerOn] = createSignal(false)

  function onWalletsApply() {
    if (access('wallets.approve')) {
      fetch('/api/pending/count').then(res2json).then(r => { setNumbers(r.data?.value ?? 0) })
    }
  }
  function onBeep() {
    playAlert() && beep()
  }
  function onGameStarted() {
    fetch(`/api/${account().game_code}/account`).then(res2json).then(r => setStore('gameAccount', reconcile(r.data ?? {})))
  }
  function onGamePlay(data) {
    setStore('reservedCloseTime', e => data?.close_time ?? e)
  }
  function onPlayersJoin() {
    if (access('players.view')) {
      fetch('/api/players/count-newly').then(res2json).then(r => { setStore('newlyPlayerNumbers', r.data?.value ?? 0) })
    }
  }

  onMount(() => {
    messenger.listen(account())
    setTimeout(() => keepalive(), 60_000)

    onWalletsApply()

    events.on(GAME_STARTED, onGameStarted)
    events.on(GAME_PLAY, onGamePlay)
    events.on(WALLETS_APPLY, onWalletsApply)
    events.on(WALLETS_APPLY, onBeep)
    events.on(WALLETS_OPERATED, onWalletsApply)
    events.on(PLAYERS_JOIN, onPlayersJoin)
    events.on(PLAYERS_JOIN, onBeep)

    if (access('game.account.edit')) {
      fetch(`/api/${account().game_code}/account`).then(res2json).then(r => setStore('gameAccount', reconcile(r.data ?? {})))
    }
    fetch(`/api/${account().game_code}/settings/close-time`).then(res2json).then(r => setStore('reservedCloseTime', e => r.data?.close_time ?? e))
  })
  onCleanup(() => {
    events.removeListener(GAME_STARTED, onGameStarted)
    events.removeListener(GAME_PLAY, onGamePlay)
    events.removeListener(WALLETS_APPLY, onBeep)
    events.removeListener(WALLETS_APPLY, onWalletsApply)
    events.removeListener(WALLETS_OPERATED, onWalletsApply)
    events.removeListener(PLAYERS_JOIN, onPlayersJoin)
    events.removeListener(PLAYERS_JOIN, onBeep)
  })

  let elMain
  const historyPushState = history.pushState
  history.pushState = function () {
    historyPushState.apply(this, arguments)
    elMain?.scrollTo(0, 0)
  }

  async function logout() {
    await unsubscribePush()
    return fetch('/api/logout').finally(() => {
      document.location.href = `/login/${account().game_code}`
    })
  }

  async function onTogglePush() {
    const enabled = !pushOn()
    const result = await togglePush()
    if (result === PUSH_OK) {
      addToast({ level: 'success', message: enabled ? '推播已开启' : '推播已关闭' })
    } else if (result === PUSH_IOS_NOT_INSTALLED) {
      addToast({ level: 'warning', message: '请先用 Safari 分享选单加入主画面，再从主画面开启后启用推播', timeout: 6000 })
    } else if (result === PUSH_UNSUPPORTED) {
      addToast({ level: 'warning', message: '此浏览器不支援推播' })
    } else if (result === PUSH_DENIED) {
      addToast({ level: 'warning', message: '通知权限未允许，无法启用推播' })
    } else if (result === PUSH_FAILED) {
      addToast({ level: 'error', message: '推播启用失败，请确认通知权限' })
    }
  }

  return (
    <>
      <div class="drawer">
        <input id="side-drawer-menu" type="checkbox" onChange={e => setDrawerOn(e.target.checked)} checked={drawerOn()} class="drawer-toggle" />
        <div class="drawer-content fixed inset-0 flex flex-col">
          {account().username === 'TBD' && (
            <header class="flex-none">
              <div class="text-center text-3xl text-error p-2">
                亲您好，您之前提出要制作【风帆机器人】键盘<br />
                我们有些事项需要与您确认，麻烦尽快 联系客服，谢谢
              </div>
            </header>
          )}
          <header class="flex-none">
            <div class="navbar bg-base-100">
              <div class="flex-1">
                <div class="flex-none lg:hidden">
                  <label for="side-drawer-menu" class="btn btn-square swap swap-rotate drawer-button">
                    <input type="checkbox" />
                    <svg class="swap-off fill-current" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 512 512"><path d="M64,384H448V341.33H64Zm0-106.67H448V234.67H64ZM64,128v42.67H448V128Z" /></svg>
                    <svg class="swap-on fill-current" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 512 512"><polygon points="400 145.49 366.51 112 256 222.51 145.49 112 112 145.49 222.51 256 112 366.51 145.49 400 256 289.49 366.51 400 400 366.51 289.49 256 400 145.49" /></svg>
                  </label>
                </div>
                <h1 class="ml-2 lg:ml-0 text-xl">永神后台{import.meta.env.MODE === 'staging' && <>(STG)</>}</h1>
                {/*account().agent !== account().username && <span class="hidden lg:block pl-1">({account().agent})</span>*/}
              </div>
              {/*<div class="flex-none">
                <div class="tooltip tooltip-bottom" data-tip="问题反馈">
                  <a href={constants.agentsCsLink} class="btn btn-ghost" target="_blank"><span class="w-5 text-success"><ChatboxEllipsesOutline /></span></a>
                </div>
              </div>*/}
              {store.gameAccount.username && (
                <div class="flex-none text-xs">剩余积分 {store.gameAccount.score.toFixed(2)}</div>
              )}
              {access('wallets.approve') && (
                <div class="flex-none">
                  <button type="button" onClick={() => onTogglePush()} class="btn btn-ghost btn-square" title={pushOn() ? '關閉推播' : '開啟推播'}>
                    <span class="w-5">{pushOn() ? <NotificationsOutline /> : <NotificationsOffOutline />}</span>
                  </button>
                </div>
              )}
              <div class="flex-none">
                <div class="dropdown dropdown-end dropdown-hover">
                  <div tabindex="0" role="button" class="btn btn-ghost"><span class="w-4"><PersonOutline /></span>{/*account().username*/}<span class="w-3"><ChevronDownOutline /></span></div>
                  <ul tabindex="0" class="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow">
                    <li><a role="button" onClick={() => window.editPassword.showModal()}><span class="w-5"><KeyOutline /></span>修改密码</a></li>
                    <li class="divider-1"></li>
                    <li><a role="button" onClick={() => logout()}><span class="w-5"><LogoutOutline /></span>退出登陆</a></li>
                  </ul>
                </div>
              </div>
            </div>
          </header>
          <div class="flex-1 flex">
            <aside class="flex-none w-48 hidden lg:block relative">
              <div class="absolute w-full h-full overflow-y-auto no-scrollbar">
                <ul class="menu gap-1">
                  <li><A href="/home"><span class="w-5"><GridOutline /></span>后台首页</A></li>
                  {access('game.account.view') && (<li><A href="/live"><span class="w-5"><PodiumOutline /></span>房间动态</A></li>)}
                  {access('wallets.approve') && (
                    <li>
                      <A href="/pending">
                        <span class="w-5"><CashOutline /></span>
                        上下分确认
                        {numbers() > 0 && (<div class="badge badge-error text-white">{numbers() >= 99 ? '+99' : numbers()}</div>)}
                      </A>
                    </li>
                  )}
                  {access('players.view') && (
                    <li>
                      <A href="/players">
                        <span class="w-5"><PeopleOutline /></span>
                        玩家列表
                        {store.newlyPlayerNumbers > 0 && (<div class="badge badge-error text-white">{store.newlyPlayerNumbers >= 99 ? '+99' : store.newlyPlayerNumbers}</div>)}
                      </A>
                    </li>
                  )}
                  {access('robots.view') && (<li><A href="/robots"><span class="w-5"><RobotSolid /></span>机器人列表</A></li>)}
                  {access('wallets.statistics.view') && (<li><A href="/statistics"><span class="w-5"><BarChartOutline /></span>积分记录</A></li>)}
                  {access('wallets.view') && (<li><A href="/wallets"><span class="w-5"><WalletOutline /></span>上下分记录</A></li>)}
                  {access('betting.view') && (<li><A href="/betting"><span class="w-5"><ReceiptOutline /></span>注单列表</A></li>)}
                </ul>
                {access('settings.edit') && (
                  <>
                    <h2 class="menu-title">系统配置</h2>
                    <ul class="menu gap-1">
                      <li><A href="/settings/game">盘口设定</A></li>
                      <li><A href="/settings/bet-ratio">赔率限额设定</A></li>
                      <li><A href="/settings/robot">机器人配置</A></li>
                      <li><A href="/settings/robot-play">机器人消息规则</A></li>
                      <li><A href="/settings/robot-timing">机器人时间规则</A></li>
                    </ul>
                  </>
                )}
                {access('staffs.view', 'staffs.edit') && (
                  <>
                    <h2 class="menu-title">系统数据</h2>
                    <ul class="menu gap-1">
                      {access('staffs.view') && (<li><A href="/staff-users"><span class="w-5"><GroupLine /></span>后台用户</A></li>)}
                    </ul>
                  </>
                )}
              </div>
            </aside>
            <main class="flex-1 relative">
              <div ref={elMain} class="absolute w-full h-full px-2 lg:pl-16 lg:pr-4 overflow-y-auto">
                {children}
                <div class="h-8"></div>
              </div>
            </main>
          </div>
          <Toast />
        </div>

        <div class="drawer-side">
          <label for="side-drawer-menu" aria-label="close sidebar" class="drawer-overlay"></label>
          <div class="bg-base-200 text-base-content min-h-full w-80">
            <div class="py-8 flex items-center justify-center bg-base-300"><h1 class="text-xl">管理后台</h1></div>
            <ul class="menu p-4 gap-1">
              <li><A href="/home" onClick={() => setDrawerOn(false)}><span class="w-5"><GridOutline /></span>后台首页</A></li>
              {access('game.account.view') && (<li><A href="/live" onClick={() => setDrawerOn(false)}><span class="w-5"><PodiumOutline /></span>房间动态</A></li>)}
              {access('wallets.approve') && (
                <li>
                  <A href="/pending" onClick={() => setDrawerOn(false)}>
                    <span class="w-5"><CashOutline /></span>
                    上下分确认
                    {numbers() > 0 && (<div class="badge badge-error text-white">{numbers() >= 99 ? '+99' : numbers()}</div>)}
                  </A>
                </li>
              )}
              {access('players.view') && (
                <li>
                  <A href="/players" onClick={() => setDrawerOn(false)}>
                    <span class="w-5"><PeopleOutline /></span>
                    玩家列表
                    {store.newlyPlayerNumbers > 0 && (<div class="badge badge-error text-white">{store.newlyPlayerNumbers >= 99 ? '+99' : store.newlyPlayerNumbers}</div>)}
                  </A>
                </li>
              )}
              {access('robots.view') && (<li><A href="/robots" onClick={() => setDrawerOn(false)}><span class="w-5"><RobotSolid /></span>机器人列表</A></li>)}
              {access('wallets.statistics.view') && (<li><A href="/statistics" onClick={() => setDrawerOn(false)}><span class="w-5"><BarChartOutline /></span>积分记录</A></li>)}
              {access('wallets.view') && (<li><A href="/wallets" onClick={() => setDrawerOn(false)}><span class="w-5"><WalletOutline /></span>上下分记录</A></li>)}
              {access('betting.view') && (<li><A href="/betting" onClick={() => setDrawerOn(false)}><span class="w-5"><ReceiptOutline /></span>注单列表</A></li>)}
            </ul>
            {access('settings.edit') && (
              <>
                <h2 class="menu-title">系统配置</h2>
                <ul class="menu p-4 gap-1">
                  <li><A href="/settings/game" onClick={() => setDrawerOn(false)}>盘口设定</A></li>
                  <li><A href="/settings/bet-ratio" onClick={() => setDrawerOn(false)}>赔率限额设定</A></li>
                  <li><A href="/settings/robot" onClick={() => setDrawerOn(false)}>机器人配置</A></li>
                  <li><A href="/settings/robot-play" onClick={() => setDrawerOn(false)}>机器人消息规则</A></li>
                  <li><A href="/settings/robot-timing" onClick={() => setDrawerOn(false)}>机器人时间规则</A></li>
                </ul>
              </>
            )}
            {access('staffs.view', 'staffs.edit') && (
              <>
                <h2 class="menu-title">系统数据</h2>
                <ul class="menu p-4 gap-1">
                  {access('staffs.view') && (<li><A href="/staff-users" onClick={() => setDrawerOn(false)}><span class="w-5"><GroupLine /></span>后台用户</A></li>)}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      <Portal mount={document.body}>
        <EditPasswordDialog />

        {(numbers() > 0 || store.newlyPlayerNumbers > 0) && (
          <div class="toast">
            {numbers() > 0 && <a href="/pending" class="alert alert-error text-white">有 {numbers()} 个新的上下分申请</a>}
            {store.newlyPlayerNumbers > 0 && <a href="/players" class="alert alert-error text-white">有 {store.newlyPlayerNumbers} 位新注册玩家</a>}
          </div>
        )}
      </Portal>
    </>
  )
}

function PendingNumbersBadge({ numbers }) {
  if (numbers > 0) {
    return (<div class="badge badge-error text-white">{numbers >= 99 ? '+99' : numbers}</div>)
  }
  return <></>
}
